import {
  serializeUploadValues,
  restoreUploadValues,
} from '../components/@material/uploadComponent/uploadTransfers';
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import {
  DEFAULT_SESSION_TTL_MS,
  isSessionDataExpired,
  loadSessionData,
  saveSessionData,
} from '../utils/sessionStorageUtils';
import {
  FORM_STATE_SESSION_KEY_PREFIX,
  FORM_SUBMITTED_KEY,
} from '../utils/sessionStorageKeys';
import { isSessionFlagTrue } from '../utils/sessionStorageUtils';

interface StoredFormState {
  form: Record<string, any>;
  savedAt: number;
}

const buildFormStateKey = (project: string | undefined) => {
  return `${FORM_STATE_SESSION_KEY_PREFIX}:${project ?? ''}`;
};

// Loads saved form data for the project unless it expired
const loadStoredForm = (project: string | undefined): Record<string, any> => {
  const formStateKey = buildFormStateKey(project);
  const stored = loadSessionData<StoredFormState>(formStateKey);
  const nav = performance.getEntriesByType?.('navigation')[0] as
    | PerformanceNavigationTiming
    | undefined;
  const wasSubmitted = isSessionFlagTrue(FORM_SUBMITTED_KEY); // back navigation to success page after submit
  if (
    !stored ||
    isSessionDataExpired(stored.savedAt, DEFAULT_SESSION_TTL_MS) ||
    (nav !== undefined && nav.type !== 'reload' && !wasSubmitted)
  )
    return {};

  // Refresh expiry on each active read
  saveSessionData(formStateKey, {
    form: stored.form,
    savedAt: Date.now(),
  });
  return restoreUploadValues(stored.form);
};

interface FormStateContextInterface {
  form: Record<string, any>;
  setForm: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  updateForm: (update: Record<string, any>) => void;
  isSubmitting: boolean;
  setIsSubmitting: (loading: boolean) => void;
}

const FormStateContext = React.createContext<FormStateContextInterface>({
  form: {},
  setForm: () => {},
  updateForm: () => {},
  isSubmitting: false,
  setIsSubmitting: () => {},
});

export const FormStateContextProvider: FunctionComponent = ({ children }) => {
  const { project } = useParams();
  const [form, setForm] = useState<Record<string, any>>(() =>
    loadStoredForm(project)
  );
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const loadedProjectRef = useRef(project);

  const updateForm = useCallback((update: Record<string, any>) => {
    setForm((prev) => ({ ...prev, ...update }));
  }, []);

  useEffect(() => {
    if (loadedProjectRef.current === project) return;
    loadedProjectRef.current = project;
    setForm(loadStoredForm(project));
  }, [project]);

  useEffect(() => {
    saveSessionData(buildFormStateKey(project), {
      form: serializeUploadValues(form),
      savedAt: Date.now(),
    });
  }, [form, project]);

  return (
    <FormStateContext.Provider
      value={{
        form,
        setForm,
        updateForm,
        isSubmitting,
        setIsSubmitting,
      }}
    >
      {children}
    </FormStateContext.Provider>
  );
};

export default FormStateContext;
